import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  type EmployeeProfile,
  updateEmployeeProfile,
} from '../../../shared/api/employee.service';
import {
  resolveApiLocale,
  resolveAppLanguage,
  type CountryCode,
} from '../../../shared/config';
import { useEmployeeSession } from '../../../shared/hooks/use-employee-session';
import {
  normalizeNamePart,
  resolveEmployeeSessionCountryCode,
  formatTaxIdInput,
  getTaxIdDigits,
  hasValidTaxIdValue,
  isTaxIdFieldError,
  resolveTaxIdErrorMessageKey,
  resolveTaxIdFieldConfig,
  type TaxIdFieldConfig,
} from '../../../shared/model';

export type PersonalDataFieldId = 'firstName' | 'lastName' | 'taxId';
export type PersonalDataFieldValues = Record<PersonalDataFieldId, string>;

type PersonalDataForm = {
  canSubmit: boolean;
  countryCode: CountryCode;
  isSubmitting: boolean;
  isTaxIdInvalid: boolean;
  setFieldValue: (fieldId: PersonalDataFieldId, nextValue: string) => void;
  submit: () => Promise<boolean>;
  submitError: string | null;
  taxIdErrorMessage: string | null;
  taxIdField: TaxIdFieldConfig;
  values: PersonalDataFieldValues;
};

type UsePersonalDataFormParams = {
  countryCode?: CountryCode;
  initialProfile?: EmployeeProfile | null;
};

const EMPLOYEE_PROFILE_QUERY_KEY = ['employee', 'profile'] as const;

function hasTextValue(value: string): boolean {
  return value.trim().length > 0;
}

function createInitialValues(
  countryCode: CountryCode,
  firstName: string | undefined,
  lastName: string | undefined,
  taxIdDigits: string | undefined,
): PersonalDataFieldValues {
  return {
    firstName: firstName ?? '',
    lastName: lastName ?? '',
    taxId: formatTaxIdInput(taxIdDigits ?? '', countryCode),
  };
}

export function canSubmitPersonalData(
  values: PersonalDataFieldValues,
): boolean {
  return (
    hasTextValue(values.firstName) &&
    hasTextValue(values.lastName) &&
    hasValidTaxIdValue(values.taxId)
  );
}

function resolveTaxIdErrorMessage(
  countryCode: CountryCode,
  error: unknown,
  invalidMessageKey: string,
  taxIdValue: string,
  t: (key: string) => string,
): string | null {
  const messageKey = resolveTaxIdErrorMessageKey(
    countryCode,
    error,
    invalidMessageKey,
    taxIdValue,
  );

  return messageKey ? t(messageKey) : null;
}

function resolveSubmitError(
  error: unknown,
  t: (key: string) => string,
): string | null {
  if (isTaxIdFieldError(error)) {
    return null;
  }

  return error ? t('login.networkError') : null;
}

export function usePersonalDataForm({
  countryCode: providedCountryCode,
  initialProfile: providedProfile,
}: UsePersonalDataFormParams = {}): PersonalDataForm {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const { authContact } = useEmployeeSession();
  const profileSave = useMutation({
    mutationFn: updateEmployeeProfile,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: EMPLOYEE_PROFILE_QUERY_KEY,
        refetchType: 'all',
      });
    },
  });
  const initialProfile = providedProfile ?? null;
  const countryCode =
    providedCountryCode ??
    resolveEmployeeSessionCountryCode(initialProfile, authContact);
  const [values, setValues] = useState(() =>
    createInitialValues(
      countryCode,
      initialProfile?.firstName,
      initialProfile?.lastName,
      initialProfile?.tin,
    ),
  );
  const taxIdField = resolveTaxIdFieldConfig(countryCode);
  const taxIdErrorMessage = resolveTaxIdErrorMessage(
    countryCode,
    profileSave.error,
    taxIdField.invalidMessageKey,
    values.taxId,
    t,
  );
  const canSubmit = canSubmitPersonalData(values);
  const isTaxIdInvalid = taxIdErrorMessage !== null;

  const setFieldValue = (fieldId: PersonalDataFieldId, nextValue: string) => {
    const shouldPreserveTaxIdError =
      fieldId !== 'taxId' && isTaxIdFieldError(profileSave.error);

    if (profileSave.isError && !shouldPreserveTaxIdError) {
      profileSave.reset();
    }

    setValues(current => ({
      ...current,
      [fieldId]:
        fieldId === 'taxId'
          ? formatTaxIdInput(nextValue, countryCode)
          : nextValue,
    }));
  };

  const submit = async () => {
    if (!canSubmit || profileSave.isPending) {
      return false;
    }

    try {
      await profileSave.mutateAsync({
        firstName: normalizeNamePart(values.firstName),
        lastName: normalizeNamePart(values.lastName),
        locale: resolveApiLocale(resolveAppLanguage(i18n.language)),
        tin: getTaxIdDigits(values.taxId),
      });

      return true;
    } catch {
      return false;
    }
  };

  return {
    canSubmit,
    countryCode,
    isSubmitting: profileSave.isPending,
    isTaxIdInvalid,
    setFieldValue,
    submit,
    submitError: resolveSubmitError(profileSave.error, t),
    taxIdErrorMessage,
    taxIdField,
    values,
  };
}
